/**
 * Client-side photo upload that posts directly to the backend.
 * Uses one HTTP request per file to `/photos/upload` so a dropped connection only loses
 * that file, not an entire batch — important on satellite / congested links.
 * Retries transient failures with exponential backoff.
 */

import { PHOTOS } from '@/shared/constants/backend-api'
import type { Photo } from '@/features/photos/actions'
import type { ApiResponse } from '@/shared/types/backend-response.type'
import {
  DEFAULT_UPLOAD_BASE_DELAY_MS,
  DEFAULT_UPLOAD_MAX_ATTEMPTS,
  executeWithUploadRetries,
  fetchUploadBearerToken,
  defaultUploadShouldRetry,
  isErrorWithStatus,
} from '@/shared/lib/upload-transient-retry'
import type { DirectUploadBatchProgress } from '@/shared/types/direct-upload-progress.type'
import {
  uploadHomeVideoMultipartChunks,
  shouldUseVideoMultipartUpload,
} from '@/features/photos/lib/upload-video-multipart'

const UNSUPPORTED_VIDEO_EXT = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
const UNSUPPORTED_VIDEO_MIME = [
  'video/x-msvideo',
  'video/x-matroska',
  'video/x-flv',
  'video/x-ms-wmv',
  'video/3gpp',
]

function parseSuccessPhoto(json: unknown): Photo | null {
  if (!json || typeof json !== 'object') return null
  const o = json as Record<string, unknown>
  if (o.success === true && o.data != null && typeof o.data === 'object') {
    return o.data as Photo
  }
  return null
}

function extractErrorMessage(json: unknown): string | undefined {
  if (!json || typeof json !== 'object') return undefined
  const rec = json as Record<string, unknown>
  if (typeof rec.message === 'string') return rec.message
  if (Array.isArray(rec.message) && rec.message.every((x) => typeof x === 'string')) {
    return rec.message.join(' ')
  }
  return undefined
}

function shouldRetryUploadError(error: unknown, attemptIndex: number): boolean {
  if (isErrorWithStatus(error)) {
    const s = error.status ?? 0
    if (s === 400 || s === 403 || s === 404 || s === 413 || s === 422) return false
  }
  return defaultUploadShouldRetry(error, attemptIndex)
}

async function uploadOneHomePhotoDirect(
  homeId: number,
  file: File,
  workOrderId?: number,
  description?: string,
  tags?: string[]
): Promise<Photo> {
  return executeWithUploadRetries(
    async () => {
      const token = await fetchUploadBearerToken()
      if (!token) {
        throw Object.assign(new Error('Not authenticated'), { status: 401 })
      }

      const formData = new FormData()
      formData.append('photo', file)
      formData.append('homeId', homeId.toString())
      if (workOrderId !== undefined) formData.append('workOrderId', workOrderId.toString())
      if (description) formData.append('description', description)
      if (tags?.length) formData.append('tags', JSON.stringify(tags))

      const res = await fetch(PHOTOS.upload, {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      })

      const json = await res.json().catch(() => ({}))

      if (!res.ok) {
        const msg =
          extractErrorMessage(json) ||
          (res.status === 413
            ? 'Request too large. Try smaller files (e.g. under 50MB per image, 500MB per video).'
            : `HTTP error! status: ${res.status}`)
        throw Object.assign(new Error(msg), { status: res.status })
      }

      const photo = parseSuccessPhoto(json)
      if (!photo) {
        const msg = extractErrorMessage(json) || 'Upload succeeded but response was unexpected.'
        throw Object.assign(new Error(msg), { status: res.status })
      }
      return photo
    },
    shouldRetryUploadError,
    { maxAttempts: DEFAULT_UPLOAD_MAX_ATTEMPTS, baseDelayMs: DEFAULT_UPLOAD_BASE_DELAY_MS }
  )
}

export async function uploadMultiplePhotosDirect(
  homeId: number,
  files: File[],
  workOrderId?: number,
  description?: string,
  tags?: string[],
  onBatchProgress?: (progress: DirectUploadBatchProgress) => void,
  onFileProgress?: (fileIndex: number, percentWithinFile: number) => void
): Promise<ApiResponse<Photo[]> & { errors?: string[]; skippedDuplicates?: string[] }> {
  try {
    if (files.length === 0) {
      return { success: false, message: 'At least one photo is required', status: 400 }
    }
    if (files.length > 30) {
      return { success: false, message: 'Maximum 30 photos can be uploaded at once', status: 400 }
    }

    for (const file of files) {
      const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? ''
      if (UNSUPPORTED_VIDEO_EXT.includes(ext)) {
        return {
          success: false,
          message: `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format. You can use free tools like HandBrake or VLC to convert your video.`,
          status: 400,
        }
      }
      if (UNSUPPORTED_VIDEO_MIME.includes(file.type.toLowerCase())) {
        return {
          success: false,
          message: `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format. You can use free tools like HandBrake or VLC to convert your video.`,
          status: 400,
        }
      }
    }

    const photos: Photo[] = []
    const errors: string[] = []
    const totalCount = files.length

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      onBatchProgress?.({
        completedCount: i,
        totalCount,
        activeIndex: i + 1,
        activeFileName: file.name,
      })
      try {
        onFileProgress?.(i, 0)
        let photo: Photo
        if (shouldUseVideoMultipartUpload(file)) {
          photo = await uploadHomeVideoMultipartChunks(
            homeId,
            file,
            {
              workOrderId,
              description,
              tags,
            },
            (chunk) => {
              const pct =
                chunk.fileSize > 0
                  ? Math.min(100, Math.round((chunk.uploadedBytes / chunk.fileSize) * 100))
                  : 0
              onBatchProgress?.({
                completedCount: i,
                totalCount,
                activeIndex: i + 1,
                activeFileName: file.name,
                currentFileBytesUploaded: chunk.uploadedBytes,
                currentFileBytesTotal: chunk.fileSize,
                currentPartNumber: chunk.partNumber,
                currentFilePartsTotal: chunk.totalParts,
              })
              onFileProgress?.(i, pct)
            }
          )
        } else {
          photo = await uploadOneHomePhotoDirect(
            homeId,
            file,
            workOrderId,
            description,
            tags
          )
        }
        photos.push(photo)
        onFileProgress?.(i, 100)
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        errors.push(`${file.name}: ${msg}`)
      }
    }

    onBatchProgress?.({
      completedCount: totalCount,
      totalCount,
      activeIndex: 0,
      activeFileName: null,
    })

    if (photos.length === 0) {
      return {
        success: false,
        message:
          errors.length > 0
            ? `All uploads failed. ${errors.slice(0, 5).join(' ')}${errors.length > 5 ? ' …' : ''}`
            : 'All uploads failed.',
        status: 500,
      }
    }

    return {
      success: true,
      data: photos,
      ...(errors.length > 0 && { errors }),
    }
  } catch (e) {
    console.error('Error uploading photos (direct):', e)
    return {
      success: false,
      message: e instanceof Error ? e.message : 'Unknown error occurred',
      status: 500,
    }
  }
}
